import React, { useState, type FC } from "react";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { CircularProgress } from "@mui/material";
import L from "leaflet";
import { Circle, CircleMarker, useMap } from "react-leaflet";
import { LeafletBarControl } from "./LeafletBarControl";
import { useCurrentLocation } from "../hooks/useCurrentLocation";
import { CURRENT_LOCATION_PANE_NAME } from "../constants/pane";

export interface CurrentLocationControlProps {
  /** コントロールボタンの配置位置（既定値: "topleft"） */
  position?: L.ControlPosition;
  /** 現在地マーカー・精度円の表示ON/OFF（既定値: true） */
  visible?: boolean;
}

const CURRENT_LOCATION_CIRCLE_OPTIONS = {
  color: "#1976d2",
  fillColor: "#2196f3",
  fillOpacity: 0.15,
  weight: 1,
  hideRadiusLabel: true,
  pane: CURRENT_LOCATION_PANE_NAME,
};

const CURRENT_LOCATION_MARKER_OPTIONS = {
  color: "#ffffff",
  fillColor: "#2196f3",
  fillOpacity: 1,
  weight: 2,
  pane: CURRENT_LOCATION_PANE_NAME,
  className: "map-export-current-location-marker",
};

/**
 * 現在地取得および地図移動を行うカスタムコントロールコンポーネント。
 *
 * ボタン押下時に現在地を取得し、精度に応じた範囲へ地図をアニメーション移動（`flyToBounds`）させるとともに、
 * 現在地マーカー（青ドット）および精度円（`<Circle>`）を描画する。
 *
 * @param {CurrentLocationControlProps} props - コンポーネントのプロパティ
 */
export const CurrentLocationControl: FC<CurrentLocationControlProps> = ({
  position = "topleft",
  visible = true,
}: CurrentLocationControlProps) => {
  const map = useMap();
  const { locate, isLocating } = useCurrentLocation();
  const [currentPosition, setCurrentPosition] = useState<{
    lat: number;
    lng: number;
    accuracy: number;
  } | null>(null);

  /**
   * 現在地ボタン押下時のイベントハンドラー。
   * 現在地を取得し、地図の移動および位置状態の更新を行う。
   *
   * @param {React.MouseEvent<HTMLAnchorElement>} e - マウスイベントオブジェクト
   */
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    if (isLocating) return;

    locate((pos) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const accuracy = pos.coords.accuracy;

      setCurrentPosition({ lat, lng, accuracy });

      if (map) {
        const bounds = L.latLng(lat, lng).toBounds(accuracy);
        map.flyToBounds(bounds, { maxZoom: 17, duration: 0.8, animate: true });
      }
    });
  };

  return (
    <>
      <LeafletBarControl position={position}>
        <a
          href="#"
          role="button"
          title="現在地に移動"
          aria-label="現在地に移動"
          onClick={handleClick}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isLocating ? "not-allowed" : "pointer",
            opacity: isLocating ? 0.6 : 1,
          }}
        >
          {isLocating ? (
            <CircularProgress size={18} thickness={5} />
          ) : (
            <MyLocationIcon sx={{ fontSize: 18, verticalAlign: "middle" }} />
          )}
        </a>
      </LeafletBarControl>

      {visible && currentPosition && (
        <>
          {/* 精度円 */}
          <Circle
            center={[currentPosition.lat, currentPosition.lng]}
            radius={currentPosition.accuracy}
            pathOptions={CURRENT_LOCATION_CIRCLE_OPTIONS}
            pane={CURRENT_LOCATION_PANE_NAME}
          />
          {/* 現在地マーカー（青ドット） */}
          <CircleMarker
            center={[currentPosition.lat, currentPosition.lng]}
            radius={7}
            pathOptions={CURRENT_LOCATION_MARKER_OPTIONS}
            pane={CURRENT_LOCATION_PANE_NAME}
          />
        </>
      )}
    </>
  );
};
